import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, Play, Sparkles, Maximize2 } from "lucide-react";
import { SITE, PORTFOLIO } from "@/data/site";
import { cardImg, heroImg } from "@/lib/img";
import { getConsent } from "@/components/CookieBanner";
import { useBrand } from "@/lib/brand";
import { asset, assetSet } from "@/lib/asset";
import SmartLink from "@/components/SmartLink";
import { fetchRealisations } from "@/lib/apiCache";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_BG = asset("/hero-bg.webp");
const HERO_BG_SET = assetSet("/hero-bg-800.webp 800w, /hero-bg.webp 1920w");

const FALLBACK_SHOWCASE = {
  title: PORTFOLIO[2].title,
  city: PORTFOLIO[2].city,
  category: PORTFOLIO[2].category,
  url: PORTFOLIO[2].url,
  // Image de secours hébergée en local : elle est le premier visuel du haut de page.
  image: asset("/showcase-fallback-640.webp"),
  srcSet: assetSet("/showcase-fallback-480.webp 480w, /showcase-fallback-640.webp 640w, /showcase-fallback-960.webp 960w"),
};

const STATS = [
  { v: "48 h", l: "Livraison ouvrées" },
  { v: "4K", l: "Photos incluses" },
  { v: "12 mois", l: "Hébergement inclus" },
  { v: "24 h/24", l: "Visite accessible" },
];

// Aperçu d'une réalisation réelle : la visite Matterport n'est chargée qu'au clic.
function ShowcasePreview({ item, playing, onLaunch, total, index, onSelect }) {
  const launch = () => {
    const consent = getConsent();
    if (consent && !consent.matterport) {
      try {
        localStorage.setItem(
          "vp-cookie-consent",
          JSON.stringify({ ...(consent || { essential: true }), matterport: true, ts: Date.now() })
        );
      } catch (e) { /* ignore */ }
    }
    onLaunch();
  };

  return (
    <div
      className="surface-card relative rounded-3xl overflow-hidden aspect-[4/3] lg:aspect-[16/11]"
      data-testid="hero-showcase"
    >
      {playing ? (
        <iframe
          src={item.url}
          title={`Visite virtuelle — ${item.title}`}
          className="absolute inset-0 w-full h-full"
          allow="fullscreen; xr-spatial-tracking"
          loading="lazy"
          data-testid="hero-showcase-iframe"
        />
      ) : (
        <>
        <button
          type="button"
          onClick={launch}
          data-testid="hero-showcase-play"
          aria-label={`Explorer la visite virtuelle ${item.title}`}
          className="group absolute inset-0 w-full h-full text-left"
        >
          <img
              key={item.title}
              {...heroImg(item.image, "16x9", "(min-width: 1024px) 45vw, 92vw")}
              {...(item.srcSet ? { srcSet: item.srcSet, sizes: "(min-width: 1024px) 45vw, 92vw" } : {})}
              alt={`Visite virtuelle 360 ${item.title} — ${item.city || "Hérault"}`}
              decoding="async"
              fetchPriority="high"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
            />
          <span className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/10" />

          <span className="absolute inset-0 flex items-center justify-center">
            <span
              className="relative w-20 h-20 rounded-full bg-[#E50914] text-white flex items-center justify-center transition-transform duration-200 group-hover:scale-105"
              style={{ boxShadow: "0 18px 40px -12px var(--vp-glow-strong)" }}
            >
              <Play className="w-8 h-8 fill-current translate-x-0.5" />
            </span>
          </span>

          <span
            className="absolute top-4 left-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full bg-black/65 border border-white/15"
            style={{ color: "#ffffff" }}
          >
            Visite réelle · {item.category}
          </span>

          <span className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
            <span className="block text-xs" style={{ color: "#d4d4d8" }}>{item.city}</span>
            <span className="block font-display text-xl md:text-2xl font-semibold tracking-tight" style={{ color: "#ffffff" }}>
              {item.title}
            </span>
            <span
              className="mt-3 inline-flex items-center gap-2 text-sm font-medium bg-white/10 border border-white/20 rounded-full px-3.5 py-1.5 backdrop-blur-sm transition-colors group-hover:bg-[#E50914] group-hover:border-[#E50914]"
              style={{ color: "#ffffff" }}
            >
              <Maximize2 className="w-3.5 h-3.5" /> Explorer la visite
            </span>
          </span>
        </button>

        {total > 1 && (
          <div
            className="absolute top-4 right-4 flex items-center gap-1.5"
            data-testid="hero-showcase-dots"
          >
            {Array.from({ length: total }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onSelect(i)}
                aria-label={`Afficher la réalisation ${i + 1}`}
                aria-current={i === index}
                data-testid={`hero-showcase-dot-${i}`}
                className="min-h-[40px] min-w-[28px] px-2 flex items-center justify-center"
              >
                <span
                  className={`block h-1.5 rounded-full transition-all duration-300 ${
                    i === index ? "w-5 bg-[#E50914]" : "w-1.5 bg-white/40 hover:bg-white/70"
                  }`}
                />
              </button>
            ))}
          </div>
        )}
        </>
      )}
    </div>
  );
}

const ROTATE_MS = 6500;
const MAX_SHOWCASE = 6;

export default function Hero() {
  const [items, setItems] = useState([FALLBACK_SHOWCASE]);
  const brand = useBrand();
  const [index, setIndex] = useState(0);
  const [rotating, setRotating] = useState(false);
  const [playing, setPlaying] = useState(false);

  // Le visuel d'ouverture reste l'image locale préchargée (plus grand élément du premier écran) ;
  // les visites réelles viennent ensuite dans le carrousel, sans retarder l'affichage.
  useEffect(() => {
    let alive = true;
    const run = () =>
      fetchRealisations("hero")
        .then((data) => {
          const list = (data || []).filter((x) => x.url && x.image).slice(0, MAX_SHOWCASE - 1);
          if (alive && list.length) setItems([FALLBACK_SHOWCASE, ...list]);
        })
        .catch(() => {});
    const schedule = () =>
      window.requestIdleCallback ? requestIdleCallback(run, { timeout: 2500 }) : setTimeout(run, 900);
    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });
    return () => { alive = false; };
  }, []);

  // La rotation ne démarre qu'après la première interaction (ou 6 s) : le premier visuel reste stable.
  useEffect(() => {
    if (playing || items.length < 2 || rotating) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const go = () => setRotating(true);
    const events = ["scroll", "pointerdown", "keydown", "touchstart"];
    events.forEach((e) => window.addEventListener(e, go, { passive: true, once: true }));
    const t = setTimeout(go, 6000);
    return () => {
      clearTimeout(t);
      events.forEach((e) => window.removeEventListener(e, go));
    };
  }, [playing, items.length, rotating]);

  useEffect(() => {
    if (playing || !rotating || items.length < 2) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % items.length), ROTATE_MS);
    return () => clearInterval(id);
  }, [playing, rotating, items.length]);

  const item = items[index] || FALLBACK_SHOWCASE;

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-28 pb-16 md:pt-32 md:pb-20 lg:min-h-[88vh] flex items-center"
    >
      {/* Les images du premier écran sont préchargées depuis index.html : découvertes avant même le JS. */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          srcSet={HERO_BG_SET}
          sizes="100vw"
          alt=""
          aria-hidden="true"
          width="1920"
          height="1080"
          fetchPriority="high"
          className="w-full h-full object-cover opacity-70"
          decoding="async"
        />
        <div className="absolute inset-0 hero-vignette" />
        <div className="absolute inset-0 grid-bg opacity-20" />
      </div>

      <div className="relative vp-shell px-6 md:px-10 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div
            className="lg:col-span-6 xl:col-span-6 vp-rise"
          >
            <div className="overline mb-4 flex items-center gap-2.5" data-testid="hero-overline">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Hérault · Matterport Pro3</span>
            </div>

            <h1
              data-testid="hero-title"
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-5 vp-measure"
            >
              Faites entrer vos clients{" "}
              <span className="accent-red">avant qu'ils ne franchissent la porte.</span>
            </h1>

            <p
              data-testid="hero-subtitle"
              className="text-base md:text-lg text-zinc-300 leading-relaxed max-w-xl"
            >
              VirtualPeek réalise vos <strong className="font-medium text-white">visites virtuelles
              Matterport</strong> dans l'Hérault — immobilier, commerces et hébergements.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <SmartLink href="/#contact" className="btn-primary" data-testid="hero-cta-quote">
                Demander un devis <ArrowRight className="w-4 h-4" />
              </SmartLink>
              <a href="#portfolio" className="btn-secondary" data-testid="hero-cta-portfolio">
                <Play className="w-4 h-4" /> Explorer une visite
              </a>
            </div>

            <dl className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-5 md:gap-6 max-w-xl">
              {STATS.map((s, i) => (
                <div
                  key={s.l}
                  className="vp-rise"
                  style={{ animationDelay: `${100 + i * 50}ms` }}
                  data-testid={`hero-stat-${i}`}
                >
                  <dt className="font-display text-2xl md:text-3xl font-semibold text-white">{s.v}</dt>
                  <dd className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mt-1">{s.l}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div
            className="lg:col-span-6 xl:col-span-6 relative vp-rise"
            style={{ animationDelay: "100ms" }}
          >
            <ShowcasePreview
              item={item}
              playing={playing}
              onLaunch={() => setPlaying(true)}
              total={items.length}
              index={index}
              onSelect={setIndex}
            />

            <img
              src={asset(brand.gold ? "/mascot-or-480.webp" : "/mascot-hero-480.webp")}
              alt="Mascotte VirtualPeek — caméra 360"
              loading="lazy"
              width="192"
              height="318"
              className="hidden xl:block absolute -bottom-10 -right-6 w-40 2xl:w-48 pointer-events-none select-none vp-mascot-glow"
              data-testid="hero-mascot"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
